/**
 * shipping-pill custom element — keeps the free-shipping pill in sync with the price of
 * the selected variant.
 *
 * WHY IT EXISTS: Impact's <product-rerender allow-partial-rerender> only swaps a
 * hard-coded whitelist of block types on a variant change (price, buy-buttons,
 * liquid, ...), so a new block type goes stale the moment the shopper switches
 * variants — and this pill is one of the few that MUST change with them: a $65 size
 * and a $169 size sit either side of the threshold and the sentence is different on
 * each. Rather than patch the theme's minified bundle, this element does for itself
 * exactly what the theme does for whitelisted blocks: listen for the form's
 * `product:rerender` event and replace itself with its counterpart from the
 * server-rendered fragment. The threshold comparison therefore stays server-side, in
 * Liquid, next to the variant's real price; this script decides nothing.
 *
 * A DELIBERATE SIBLING OF assets/ships-by-pill.js, not a shared base class. The two are
 * the same eight lines of re-render seam and nothing else — that pill also refreshes a
 * cached date from the shopper's clock, this one has no clock-dependent content at all —
 * and a shared module would have to be loaded by both snippets, which turns a broken
 * import into two dead pills instead of one. The contract test pins both copies to the
 * same behaviour, which is what keeps them from drifting.
 *
 * Binding once to the form node at connect is safe by the theme's own design: the
 * <form is="product-form"> is rendered OUTSIDE the swapped <buy-buttons> element
 * (snippets/buy-buttons.liquid nests buy-buttons inside the form), and Impact's own
 * ProductRerender/ProductGallery bind to the form once at connect the same way — if the
 * form node were replaced on variant change, the theme's whole event chain would already
 * be broken. On `productChange` events the theme replaces the whole product-info subtree
 * itself (this element included), so we do nothing and let the fresh element reconnect.
 */
class ShippingPill extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const form = document.forms[this.getAttribute("form")];
    form?.addEventListener("product:rerender", (event) => this._onRerender(event), {
      signal: this._abortController.signal,
    });
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  _onRerender(event) {
    // A bare CustomEvent from an app embed or future customization carries no detail;
    // never let it throw away the pill's only sync mechanism.
    if (!event.detail || !event.detail.htmlFragment || event.detail.productChange) {
      return;
    }
    const replacement = this._findInFragment(event.detail.htmlFragment);
    if (replacement) {
      this.replaceWith(replacement);
    }
  }

  /**
   * The fragment can nest content inside <template> elements (the theme's own
   * deepQuerySelector exists for the same reason), so recurse into template contents
   * when the top-level lookup misses.
   */
  _findInFragment(root) {
    const hit = root.querySelector(`shipping-pill[id="${CSS.escape(this.id)}"]`);
    if (hit) {
      return hit;
    }
    for (const template of root.querySelectorAll("template")) {
      const nested = this._findInFragment(template.content);
      if (nested) {
        return nested;
      }
    }
    return null;
  }
}

if (!window.customElements.get("shipping-pill")) {
  window.customElements.define("shipping-pill", ShippingPill);
}
